/**
 * FDRInvitationsList — plan-admin view of every invitation issued by the
 * caller's organization, with status filters and a revoke action.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { SCRUBBER_API_BASE } from '../../api/config';
import { csrfFetch } from '../../api/csrf';
import FDRInviteModal from './FDRInviteModal';

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  used:    'bg-emerald-100 text-emerald-700 border-emerald-200',
  revoked: 'bg-rose-100 text-rose-700 border-rose-200',
  expired: 'bg-slate-100 text-slate-600 border-slate-200',
};

export default function FDRInvitationsList() {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [protocolFilter, setProtocolFilter] = useState('');
  const [showInvite, setShowInvite] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter)   params.set('status', statusFilter);
      if (protocolFilter) params.set('protocol', protocolFilter);
      const res = await fetch(`${SCRUBBER_API_BASE}/api/fdr/invitations?${params.toString()}`, {
        credentials: 'include',
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.message || 'Could not load invitations.');
        return;
      }
      setInvitations(json.invitations || []);
    } catch (e) {
      setError(e.message || 'Could not load invitations.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, protocolFilter]);

  useEffect(() => { load(); }, [load]);

  async function revoke(id) {
    if (!window.confirm('Revoke this invitation? The FDR will no longer be able to submit.')) return;
    try {
      const res = await csrfFetch(`${SCRUBBER_API_BASE}/api/fdr/invitations/${encodeURIComponent(id)}/revoke`, {
        method: 'POST',
        credentials: 'include',
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        alert(json.message || 'Could not revoke.');
        return;
      }
      load();
    } catch (e) {
      alert(e.message || 'Could not revoke.');
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="flex items-end justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">FDR Submission Portal</div>
          <h1 className="text-2xl font-bold mt-1">Invitations</h1>
          <p className="text-sm text-slate-500 mt-1">Temporary, single-use portal links you’ve issued to FDRs.</p>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          className="bg-rose-600 hover:bg-rose-700 text-white font-semibold px-4 py-2 rounded-lg text-sm shadow-sm"
        >
          + New invitation
        </button>
      </header>

      <div className="flex gap-2 mb-4">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="used">Used</option>
          <option value="revoked">Revoked</option>
          <option value="expired">Expired</option>
        </select>
        <select value={protocolFilter} onChange={(e) => setProtocolFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
          <option value="">All protocols</option>
          {['ODAG', 'CDAG', 'FA', 'SNPCC', 'CPE'].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <button onClick={load} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm hover:bg-slate-50">Refresh</button>
      </div>

      {error && <div className="mb-3 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading && <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>}
        {!loading && invitations.length === 0 && (
          <div className="p-10 text-center text-slate-500 text-sm">No invitations yet. Click “New invitation” to send one.</div>
        )}
        {!loading && invitations.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">FDR</th>
                <th className="text-left px-4 py-2 font-semibold">Protocol / Universe</th>
                <th className="text-left px-4 py-2 font-semibold">Period</th>
                <th className="text-left px-4 py-2 font-semibold">MFA</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-left px-4 py-2 font-semibold">Expires</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invitations.map((inv) => (
                <tr key={inv.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">{inv.fdr_organization_name}</div>
                    <div className="text-xs text-slate-500 font-mono">{inv.fdr_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{inv.protocol}</div>
                    <div className="text-xs text-slate-500 font-mono">{inv.universe_type}</div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">
                    {inv.universe_period_start} → {inv.universe_period_end}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {inv.mfa_channel === 'sms' ? <span>SMS · <span className="font-mono">{inv.fdr_phone_masked || '(unset)'}</span></span> : <span>Email OTP</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded border ${STATUS_STYLES[inv.status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{new Date(inv.expires_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    {inv.status === 'pending' && (
                      <button onClick={() => revoke(inv.id)} className="text-rose-600 hover:text-rose-700 text-xs font-semibold">
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <FDRInviteModal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        onCreated={() => { setShowInvite(false); load(); }}
      />
    </div>
  );
}
